import type { FC, FormEvent } from "react";
import type { AbstractSummary } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface ListViewProps {
	searchQuery: string;
	results: AbstractSummary[];
	loading: boolean;
	onSearchChange: (query: string) => void;
	onSearchSubmit: (e: FormEvent) => void;
	onClearSearch: () => void;
	onSelectAbstract: (id: string) => void;
}

export const ListView: FC<ListViewProps> = ({
	searchQuery,
	results,
	loading,
	onSearchChange,
	onSearchSubmit,
	onClearSearch,
	onSelectAbstract,
}) => (
	<main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
		<form className="mb-5 flex gap-2" onSubmit={onSearchSubmit}>
			<Input
				className="h-10"
				type="search"
				aria-label="Search abstracts"
				placeholder="Search by abstract number, title, or author..."
				value={searchQuery}
				onChange={(event) => onSearchChange(event.target.value)}
			/>
			<Button variant="outline" type="submit">
				Search
			</Button>
			{searchQuery && (
				<Button variant="ghost" type="button" onClick={onClearSearch}>
					Clear
				</Button>
			)}
		</form>

		<div className="mb-3 text-xs text-muted-foreground" aria-live="polite">
			{results.length} abstracts - Select an item to view the complete abstract
		</div>

		{loading ? (
			<div className="py-8 text-center text-sm text-muted-foreground">
				Loading...
			</div>
		) : results.length === 0 ? (
			<div className="py-12 text-center text-sm text-muted-foreground">
				No results found.
			</div>
		) : (
			<div className="flex flex-col gap-1.5">
				{results.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex w-full cursor-pointer items-baseline gap-2.5 rounded-md border-none bg-transparent px-3.5 py-2.5 text-left font-inherit text-inherit transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						onClick={() => onSelectAbstract(item.id)}
					>
						<Badge
							variant="secondary"
							className="shrink-0 font-mono tabular-nums"
						>
							Poster {item.number}
						</Badge>
						<span className="text-sm leading-snug">{item.title}</span>
					</button>
				))}
			</div>
		)}
	</main>
);
